import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Divider,
  FormHelperText,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
} from "@mui/icons-material";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import systemAPI from "../../utils/systemAPI";
import userAPI from "../../utils/userAPI";

const AdminManagement = () => {
  const isValidDate = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const formatFirebaseTimestamp = (timestamp, includeTime = false) => {
    try {
      const date = new Date(timestamp);
      const dateStr = date.toISOString().split("T")[0];

      if (!includeTime) {
        return dateStr;
      }

      const timeStr = date.toISOString().split("T")[1].split(".")[0];

      return `${dateStr} ${timeStr}`;
    } catch (err) {
      console.error("Error formatting timestamp:", err);
      return "Invalid Date";
    }
  };

  const [admins, setAdmins] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [currentAdmin, setCurrentAdmin] = useState({
    name: "",
    email: "",
    universityId: "",
    departmentId: "",
  });
  const [search, setSearch] = useState("");

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [adminsRes, deptsRes] = await Promise.all([
          userAPI.getAllAdmins(),
          systemAPI.getDepartments(),
        ]);
        setAdmins(adminsRes.data || []);
        setDepartments(deptsRes.data || []);
      } catch (err) {
        setError("Failed to load admins or departments.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Filtered admins
  const filteredAdmins = admins.filter(
    (admin) =>
      admin.name.toLowerCase().includes(search.toLowerCase()) ||
      admin.email.toLowerCase().includes(search.toLowerCase()) ||
      (admin.universityId || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleOpenDialog = (mode, admin = null) => {
    setDialogMode(mode);
    if (mode === "edit" && admin) {
      setCurrentAdmin({ ...admin });
    } else {
      setCurrentAdmin({
        name: "",
        email: "",
        universityId: "",
        departmentId: "",
      });
    }
    setDialogOpen(true);
  };

  const handleInputChange = (e) => {
    setCurrentAdmin({ ...currentAdmin, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      // Find department name from departments array
      const selectedDept = departments.find(
        (d) => d.id === currentAdmin.departmentId
      );
      const department = selectedDept ? selectedDept.name : "";

      // Prepare admin payload with department information for both Firestore and custom claims
      const adminPayload = {
        ...currentAdmin,
        department: department,
        departmentId: currentAdmin.departmentId,
      };

      console.log(
        `[AdminManagement] Setting up admin with department: ${department} (${currentAdmin.departmentId})`
      );

      if (dialogMode === "add") {
        // Create admin with department claims
        const res = await userAPI.createAdmin(adminPayload);
        setAdmins([...admins, res.data.admin]);
        setSnackbar({
          open: true,
          message: "Admin created successfully with department claims",
          severity: "success",
        });

        console.log("[AdminManagement] Admin created with department claims:", {
          adminId: res.data.admin.id,
          department: department,
          departmentId: currentAdmin.departmentId,
        });
      } else {
        // Update admin with department claims
        const res = await userAPI.updateAdmin(currentAdmin.id, adminPayload);
        setAdmins(admins.map((a) => (a.id === currentAdmin.id ? res.data : a)));

        // Also update department claims if department has changed
        if (
          currentAdmin.departmentId &&
          (currentAdmin.departmentId !== res.data.departmentId ||
            department !== res.data.department)
        ) {
          console.log(
            "[AdminManagement] Department changed, updating department claims"
          );

          // Use the assign endpoint to update department claims
          await userAPI.assignAdminToDepartment(
            currentAdmin.id,
            currentAdmin.departmentId
          );
          console.log(
            "[AdminManagement] Department claims updated successfully"
          );
        }

        setSnackbar({
          open: true,
          message: "Admin updated successfully with department claims",
          severity: "success",
        });
      }
      setDialogOpen(false);
    } catch (err) {
      console.error("Error saving admin:", err);
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to save admin",
        severity: "error",
      });
    }
  };

  const handleDelete = async (adminId) => {
    if (window.confirm("Are you sure you want to delete this admin?")) {
      try {
        await userAPI.deleteAdmin(adminId);
        setAdmins(admins.filter((a) => a.id !== adminId));
        setSnackbar({
          open: true,
          message: "Admin deleted successfully",
          severity: "success",
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: "Failed to delete admin.",
          severity: "error",
        });
      }
    }
  };

  const handleSnackbarClose = () => setSnackbar({ ...snackbar, open: false });

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Typography variant="h4" gutterBottom fontWeight={700}>
        Admin Management
      </Typography>
      <Typography variant="subtitle1" gutterBottom color="text.secondary">
        Manage administrator accounts and department assignments
      </Typography>
      <Box
        sx={{
          mb: 3,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
        }}
      >
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search admins..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: "100%", sm: 300 } }}
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog("add")}
          sx={{ minWidth: 160 }}
        >
          Add Admin
        </Button>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Paper elevation={3} sx={{ borderRadius: 3, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ background: (theme) => theme.palette.grey[100] }}>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>University ID</TableCell>
                <TableCell>Department</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAdmins.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      gutterBottom
                    >
                      {admins.length === 0
                        ? "No admins found."
                        : "No results match your search."}
                    </Typography>
                    <Button
                      variant="outlined"
                      onClick={() => setSearch("")}
                      sx={{
                        mt: 2,
                        display: admins.length === 0 ? "none" : "inline-flex",
                      }}
                    >
                      Clear Search
                    </Button>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAdmins.map((admin) => (
                  <TableRow key={admin.id} hover>
                    <TableCell>{admin.name}</TableCell>
                    <TableCell>{admin.email}</TableCell>
                    <TableCell>{admin.universityId}</TableCell>
                    <TableCell>
                      {departments.find((d) => d.id === admin.departmentId)
                        ?.name || "Unassigned"}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="secondary"
                        onClick={() => handleOpenDialog("edit", admin)}
                        title="Edit Admin"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(admin.id)}
                        title="Delete Admin"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Admin Form Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {dialogMode === "add" ? "Add New Admin" : "Edit Admin"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Name"
                name="name"
                value={currentAdmin.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={currentAdmin.email}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="University ID"
                name="universityId"
                value={currentAdmin.universityId}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Department</InputLabel>
                <Select
                  name="departmentId"
                  value={currentAdmin.departmentId}
                  onChange={handleInputChange}
                  label="Department"
                >
                  <MenuItem value="">Unassigned</MenuItem>
                  {departments.map((dept) => (
                    <MenuItem key={dept.id} value={dept.id}>
                      {dept.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="active"
                  value={
                    currentAdmin.active === undefined
                      ? true
                      : currentAdmin.active
                  }
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value={true}>Active</MenuItem>
                  <MenuItem value={false}>Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !currentAdmin.name ||
              !currentAdmin.email ||
              !currentAdmin.universityId
            }
          >
            {dialogMode === "add" ? "Create Admin" : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminManagement;
